import { useCallback } from 'react';
import { selectCreditApplication } from 'state/creditApplication';
import { useSelector, useStore } from 'react-redux';
import { values as valuesOf, all, pick } from 'ramda';
import isPresent from 'utils/isPresent';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import { selectTenant } from 'state/tenant';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useQueryParameters from 'hooks/useQueryParameters';

import useConsumerSearchRequest, {
  ConsumerSearchError,
} from 'hooks/requests/consumerSearch';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';

interface Values {
  ssn: string;
  zip: string;
  mobilePhone: string;
  otpCode?: string;
}
const useConsumerSearchWithNavigation = () => {
  const store = useStore();
  const { setAnalyticsVariables } = useAnalyticVariables();
  const [searchConsumerRequest] = useConsumerSearchRequest();
  const {
    trustedBrandPrefill,
    overrideConfig,
    enabledFeatures: { requireAdditionalVerification },
  } = useSelector(selectTenant);
  const { push } = useNavigationHelpers();
  const query = useQueryParameters();

  const searchConsumer = useCallback(
    async (values: Values, nextPath: string) => {
      // TODO: this replicates ConsumerSearch call in the ConsumerSearch feature.
      // This should be refactored for both of them to share the same behavior.
      const creditApplication = selectCreditApplication(store.getState());
      const prefillKeys = [
        'firstName',
        'lastName',
        'address1',
        'city',
        'state',
        'zip',
        'mobilePhone',
      ];
      try {
        const isSufficientPrefill = all(
          isPresent,
          valuesOf(pick(prefillKeys, creditApplication)),
        );
        if (isSufficientPrefill) {
          setAnalyticsVariables({ isSufficientPrefill: 'true' });
        }
        if (
          overrideConfig.phoneLookup &&
          trustedBrandPrefill &&
          isSufficientPrefill
        ) {
          push(nextPath);
          return;
        }

        const params = {
          ...values,
          carrier: query.carrier,
          keyword: query.keyword,
          shortCode: query.shortCode,
          token: query.token,
        };

        const { hasExistingAccount } = await searchConsumerRequest(params);

        if (hasExistingAccount) {
          push('/existing-account');
        } else {
          push(nextPath);
        }
      } catch (e) {
        if (catchOnly(e, NonClientError, ConsumerSearchError)) {
          if (e instanceof NonClientError || e instanceof ConsumerSearchError) {
            if (
              requireAdditionalVerification &&
              requireAdditionalVerification.requirePrefill
            ) {
              push('/require-additional-verification');
            } else if (query.token && requireAdditionalVerification) {
              push('/require-additional-verification');
            } else {
              push(nextPath);
            }
          }
        }
      }
    },
    [
      store,
      overrideConfig.phoneLookup,
      trustedBrandPrefill,
      query.carrier,
      query.keyword,
      query.shortCode,
      query.token,
      searchConsumerRequest,
      setAnalyticsVariables,
      push,
      requireAdditionalVerification,
    ],
  );

  return searchConsumer;
};
export default useConsumerSearchWithNavigation;
